<template>
  <div class="flex items-center">
    <customer-alerts v-if="!hideCustomerAlerts && reservation.customer" :customer="reservation.customer">
      <o-icon :size="16" class="mb-1 mr-3 text-red-500" icon="warning" />
    </customer-alerts>

    <o-chip v-if="isCustomerConfirmed" :size="size" variant="success" class="mr-1 mb-1">
      {{ $t('reservation.attending') }}
    </o-chip>
    <o-chip v-if="isStatusConfirmed" :size="size" class="mr-1 mb-1" variant="success">
      {{ $t('reservation.confirmed') }}
    </o-chip>
    <o-chip v-if="isStatusUnconfirmed" :size="size" class="mr-1 mb-1" variant="warning">
      {{ $t('reservation.unconfirmed') }}
    </o-chip>
    <o-chip v-if="isStatusCancelled" :size="size" class="mr-1 mb-1" variant="danger">
      {{ $t('general.cancelled') }}

      <div v-if="_reservation.cancellationType || _reservation.cancellationNote" class="ml-1">
        <p v-if="_reservation.cancellationType">
          {{ _reservation.cancellationType.shortName }}
        </p>

        <p v-if="_reservation.cancellationNote" class="ml-1">
          ({{ _reservation.cancellationNote }})
        </p>
      </div>
    </o-chip>

    <o-chip v-if="isDeleted" :size="size" class="mr-1 mb-1" variant="danger">
      {{ $t('general.deleted') }}
    </o-chip>
    <o-chip v-if="isPaid && !isGroup" :size="size" flat class="mb-1 mr-1" variant="success">
      {{ $t('general.paid') }}
    </o-chip>

    <template v-if="editable">
      <o-chip
        v-if="isCheckedIn && !isGroup"
        :size="size"
        flat
        class="mb-1 mr-1 flex-shrink-0"
        variant="info"
      >
        {{ $t('reservation.checkedIn') }}
        <!-- Undo check in -->
      </o-chip>
      <o-chip
        v-if="isNoShow && !isGroup"
        :size="size"
        class="mb-1 mr-1 flex-shrink-0"
        variant="warning"
      >
        {{ $t('reservation.noShow') }}
        <!-- Undo no show -->
      </o-chip>
    </template>

    <template v-if="!editable">
      <o-chip
        v-if="isCheckedIn && !isGroup"
        :size="size"
        flat
        class="mb-1 mr-1 flex-shrink-0"
        variant="info"
      >
        Checked in
        <!-- Undo check in -->
      </o-chip>
      <o-chip
        v-if="isNoShow && !isGroup"
        :size="size"
        class="mb-1 mr-1 flex-shrink-0"
        variant="warning"
      >
        {{ $t('reservation.noShow') }}
        <!-- Undo no show -->
      </o-chip>
    </template>

    <o-chip v-if="!compact && isBookedOnline" :size="size" class="mr-1 mb-1">
      Online
    </o-chip>

    <o-chip v-if="!compact && hasPaymentDetailsCaptured" :size="size" flat class="mr-1 mb-1" variant="success">
      {{ $t('reservation.paymentDetailsCaptured') }}
    </o-chip>
    <o-chip v-if="!compact && onlineTermsAgreed" :size="size" flat class="mr-1 mb-1" variant="success">
      {{ $t('reservation.termsAgreed') }}
    </o-chip>
    <o-chip v-if="hasBouncedEmail" :size="size" flat class="mr-1 mb-1" variant="warning">
      {{ $t('general.emailBounced') }}
    </o-chip>

    <!-- Reservation customer request type -->
    <template v-if="!compact && !isGroup">
      <status-request-type v-model="_reservation.customerRequestType" :editable="editable" :size="size" class="mb-1 mr-1" />
    </template>

    <!-- Reservation custom status type -->
    <template v-if="!isGroup">
      <status-custom v-model="_reservation.statusType" :reservation="reservation" :editable="editable" :size="size" class="mb-1 mr-1" />
    </template>
  </div>
</template>

<script setup lang="ts">
import StatusCustom from './StatusCustom.vue'
import StatusRequestType from './StatusRequestType.vue'
import CustomerAlerts from '@/components/customer/elements/Alerts.vue'

interface Props {
  reservation?: Record<string, any> | null
  loading?: boolean
  editable?: boolean
  compact?: boolean
  size?: string | null
  hideCustomerAlerts?: boolean
}

interface Emits {
  (e: 'update:reservation', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  reservation: null,
  loading: false,
  editable: false,
  compact: false,
  size: null,
  hideCustomerAlerts: false,
})

const emit = defineEmits<Emits>()

const _reservation = computed({
  get () {
    return props.reservation
  },
  set (value) {
    emit('update:reservation', value)
  },
})

const isCheckedIn = computed(() => props.reservation!.checkedIn)
const isNoShow = computed(() => props.reservation!.noShow)
const isDeleted = computed(() => props.reservation!.deleted)
const isPaid = computed(() => props.reservation!.isPaid)
const isGroup = computed(() => props.reservation!.isGroup)
const isBookedOnline = computed(() => props.reservation!.wasBookedOnline)
const isStatusConfirmed = computed(() => props.reservation!.isStatusConfirmed)
const isStatusUnconfirmed = computed(() => props.reservation!.isStatusUnconfirmed)
const isStatusCancelled = computed(() => props.reservation!.isStatusCancelled)
const isCustomerConfirmed = computed(() => props.reservation!.customerConfirmed)
const onlineTermsAgreed = computed(() => props.reservation!.onlineTermsAgreed)
const hasPaymentDetailsCaptured = computed(() => props.reservation!.paymentDetailsCaptured)
const hasPass = computed(() => !!props.reservation!.customerPass)
const hasBouncedEmail = computed(() => props.reservation!.customer && !!props.reservation!.customer.emailBounced)
const statusLabel = computed(() => props.reservation!.statusLabel.length ? props.reservation!.statusLabel : props.reservation!.status.display)
</script>
